'use client'

import { AlertCircle } from 'lucide-react'
import { useCallback, useState } from 'react'
import {
  type DefaultValues,
  type FieldValues,
  type Resolver,
  type SubmitErrorHandler,
  type SubmitHandler,
  type UseFormHandleSubmit,
  type UseFormProps,
  useForm as useReactHookForm,
} from 'react-hook-form'

import { useToast } from '@/components/ui'

export type UseHookFormProps<TDefaultValues extends FieldValues> = {
  values?: UseFormProps<TDefaultValues>['values']
  defaultValues?: DefaultValues<TDefaultValues>
  resolver?: Resolver<TDefaultValues>
}

export const useForm = <TDefaultValues extends FieldValues>({
  values,
  defaultValues,
  resolver,
}: UseHookFormProps<TDefaultValues>) => {
  const { toast } = useToast()

  const form = useReactHookForm<TDefaultValues>({
    mode: 'all',
    defaultValues,
    values,
    resolver,
  })

  const {
    formState: { isSubmitting, isValidating, isLoading },
    handleSubmit,
  } = form

  const [buttonDisabled, setButtonDisabled] = useState(false)

  const onSubmit: UseFormHandleSubmit<TDefaultValues> = useCallback(
    (successCallback: SubmitHandler<TDefaultValues>) => {
      const errorCallback: SubmitErrorHandler<TDefaultValues> = (
        _,
        event?: React.BaseSyntheticEvent,
      ) => {
        try {
          if (event?.preventDefault) {
            event?.preventDefault()
          }
          setButtonDisabled(true)
          toast({
            title: 'Preencha os campos obrigatórios',
            variant: 'destructive',
            icon: AlertCircle,
          })
        } finally {
          setButtonDisabled(false)
        }
      }

      const successCallbackWrapper = async (
        data: TDefaultValues,
        event?: React.BaseSyntheticEvent,
      ) => {
        try {
          if (event?.preventDefault) {
            event.preventDefault()
          }
          setButtonDisabled(true)
          await successCallback(data, event)
        } finally {
          setButtonDisabled(false)
        }
      }

      return handleSubmit(successCallbackWrapper, errorCallback)
    },
    [handleSubmit, toast],
  )

  return {
    ...form,
    handleSubmit: onSubmit,
    buttonDisabled: buttonDisabled || isSubmitting || isValidating || isLoading,
  }
}
